import { Avatar, Tag, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import { minutesToHours } from "date-fns"
import { useCallback, useMemo, useState } from "react"

import { API } from "@/api"
import { APIQueries } from "@/api/queries"
import {
  Grade,
  OrganizationUserType,
  Status,
} from "@/api/resources/teachers/types"
import Breadcrumbs from "@/components/breadcrumbs"
import { DataTable } from "@/components/data-table"
import { SortConfig } from "@/components/data-table/types"
import { GradeDisplay } from "@/components/grade-display"
import { GRADE_LEVELS } from "@/components/grade-display/utils"
import PageContainer from "@/components/page-container"
import { getByPath, renderAnalyticsMetric } from "@/features/teachers/utils"
import { useClubId } from "@/lib/hooks/use-club-id"
import { useOrgId } from "@/lib/hooks/use-org-id"

const Teachers = () => {
  const params = { fields: ["analytics"] }

  const orgId = useOrgId()

  const orgUserListQuery = useQuery({
    queryKey: APIQueries.teachers.organizationUserList({
      params: {
        ...params,
      },
      orgId: orgId,
    }).queryKey,
    queryFn: async () => {
      return API.teachers.organizationUserList({
        params,
        urlParams: { orgId: orgId },
      })
    },
  })

  const [query, setQuery] = useState<{
    search?: string
    sort?: SortConfig | null
  }>({
    search: "",
    sort: null,
  })

  const handleQueryChange = useCallback(
    (params: { search?: string; sort?: SortConfig | null }) => {
      setQuery((prev) => ({
        ...prev,
        ...params,
      }))
    },
    [],
  )

  const filteredData = useMemo(() => {
    const list = orgUserListQuery.data
    if (!list || list.length === 0) return []

    const search = query.search?.toLowerCase().trim()
    if (!search) return list

    return list.filter((user) => {
      const haystack = [
        user.name,
        user.analytics?.attempt?.band,
        user.analytics?.totalLearningMinutes
          ? Math.floor(
              minutesToHours(user.analytics.totalLearningMinutes),
            ).toString()
          : null,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()

      return haystack.includes(search)
    })
  }, [orgUserListQuery.data, query.search])

  const isGrade = (value: unknown): value is Grade =>
    Object.values(Grade).includes(value as Grade)

  const sortedData = useMemo(() => {
    if (!query.sort) return filteredData

    const { key, direction } = query.sort

    const compare = (a: OrganizationUserType, b: OrganizationUserType) => {
      if (key === "analytics.attempt.band") {
        const aBand = a.analytics?.attempt?.band
        const bBand = b.analytics?.attempt?.band

        const aLevel = isGrade(aBand) ? GRADE_LEVELS[aBand] : 0
        const bLevel = isGrade(bBand) ? GRADE_LEVELS[bBand] : 0

        return direction === "asc" ? aLevel - bLevel : bLevel - aLevel
      }

      const aVal = getByPath(a, key)
      const bVal = getByPath(b, key)

      if (aVal == null && bVal == null) return 0
      if (aVal == null) return direction === "asc" ? -1 : 1
      if (bVal == null) return direction === "asc" ? 1 : -1

      if (typeof aVal === "string" && typeof bVal === "string") {
        return direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "asc" ? aVal - bVal : bVal - aVal
      }

      return direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    }

    return [...filteredData].sort(compare)
  }, [filteredData, query.sort])

  const clubId = useClubId()

  const organizationParams = { fields: ["name"] }

  const currentOrganization = useQuery({
    queryKey: APIQueries.organization.retrieve(
      { id: orgId },
      organizationParams,
    ).queryKey,
    queryFn: () =>
      API.organization.retrieve({
        urlParams: {
          id: orgId,
        },
      }),

    refetchOnMount: false,
  })

  return (
    <>
      {currentOrganization.data && (
        <div className="w-full border-b py-3">
          <PageContainer>
            <Breadcrumbs className="mb-2" />
            <Typography className="text-[32px] font-bold leading-[100%] tracking-tight">
              {clubId === currentOrganization.data?.id.toString()
                ? "Directly Enrolled Teachers"
                : currentOrganization?.data?.name}
            </Typography>
          </PageContainer>
        </div>
      )}
      <PageContainer>
        <div className="flex flex-col">
          <>
            <DataTable<OrganizationUserType>
              showSearch
              onRowClick={() => {}}
              searchPlaceholder="Search Entities"
              data={sortedData}
              headerClassName="bg-onSurface-900 text-surface"
              onQueryChange={handleQueryChange}
              loading={
                orgUserListQuery.isLoading || currentOrganization?.isLoading
              }
              columns={[
                {
                  key: "name",
                  label: "Name",
                  sortable: true,
                  width: "300px",
                  render: (_, row) => {
                    return (
                      <div className="flex items-center gap-1.5">
                        <Avatar
                          name={row.name}
                          className="size-5"
                          src={row.picture}
                        />
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1">
                            <Typography
                              variant="strongSmallBody"
                              className="first-letter-capitalize"
                            >
                              {row.name}
                            </Typography>
                            {/* @ts-expect-error - comparing with Status.Invited intentionally */}
                            {row.status === Status.Invited && (
                              <Tag
                                color="warning"
                                size="sm"
                                className="bg-warning-100 px-0.5 text-xs font-medium text-warning-700"
                              >
                                Pending
                              </Tag>
                            )}
                          </div>
                          <Typography className="text-[12px] font-normal leading-[130%] text-onSurface-500">
                            {row.email}
                          </Typography>
                        </div>
                      </div>
                    )
                  },
                },
                {
                  width: "145px",
                  key: "analytics.totalLearningMinutes",
                  label: "Learning Hours",
                  sortable: true,
                  render: (val: number) => {
                    if (val == null) return "-"
                    return (
                      <>
                        {renderAnalyticsMetric({
                          value: val,
                          render: ({ formattedValue }) => (
                            <Typography className="text-[14px] font-semibold leading-[130%] tracking-normal text-onSurface-700">
                              {formattedValue}
                            </Typography>
                          ),
                          renderInTime: true,
                        })}
                      </>
                    )
                  },
                },
                {
                  width: "180px",
                  key: "analytics.attempt.band",
                  label: `Band`,
                  sortable: true,
                  render: (val) => {
                    if (!val) return "-"
                    return <GradeDisplay grade={val} />
                  },
                },
              ]}
            />
          </>
        </div>
      </PageContainer>
    </>
  )
}

export default Teachers
